import * as React from "react";

import { cn } from "../../../lib";

export interface InputProps extends Omit<
  React.ComponentProps<"input">,
  "size"
> {
  description?: React.ReactNode;
  error?: React.ReactNode;
  label?: React.ReactNode;
  variant?: "default" | "dark" | "light";
  size?: "sm" | "md" | "lg";
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  containerClassName?: string;
  labelClassName?: string;
  labelStyle?: React.CSSProperties;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      id,
      description,
      error,
      label,
      variant = "default",
      size = "md",
      helperText,
      leftIcon,
      rightIcon,
      containerClassName,
      labelClassName,
      labelStyle,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      ...props
    },
    ref,
  ) => {
    const hasError = Boolean(error);
    const errorMessage =
      typeof error === "string" || React.isValidElement(error)
        ? error
        : undefined;
    const helperContent = description ?? helperText;
    const generatedId = React.useId();
    const inputId = id ?? `input-${generatedId}`;
    const descriptionId = helperContent ? `${inputId}-description` : undefined;
    const errorId = errorMessage ? `${inputId}-error` : undefined;
    const describedBy = [ariaDescribedBy, errorId ?? descriptionId]
      .filter(Boolean)
      .join(" ");

    const baseClasses =
      "flex h-[var(--control-height)] w-full rounded-sm border bg-background px-3 py-1.5 text-[length:var(--font-size-product-ui)] text-foreground shadow-sm transition-colors file:border-0 file:bg-transparent file:text-[length:var(--font-size-product-ui)] file:font-normal file:text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none read-only:bg-muted read-only:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none forced-colors:border-[FieldText]";

    const variantClasses = {
      default:
        "border-input bg-background text-foreground focus-visible:ring-ring",
      dark: "border-gray-600 bg-gray-800 text-white placeholder-gray-400 focus-visible:ring-ring",
      light:
        "border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus-visible:ring-ring",
    };

    const sizeClasses = {
      sm: "h-8 px-2 py-1",
      md: "h-[var(--control-height)] px-3 py-1",
      lg: "h-[var(--touch-target-minimum)] px-4 py-2 text-base",
    };

    const inputClasses = cn(
      baseClasses,
      variantClasses[variant],
      sizeClasses[size],
      hasError && "border-error focus-visible:ring-error",
      leftIcon && "ps-10",
      rightIcon && "pe-10",
      className?.includes("border-t-0") && "border-t-0",
      className?.includes("border-l-0") && "border-l-0",
      className?.includes("rounded-l-none") && "rounded-l-none",
      className,
    );

    const containerClasses = cn("w-full", containerClassName);
    const labelClasses = cn(
      "block text-[length:var(--font-size-product-ui)] font-normal text-foreground mb-1.5",
      labelClassName,
    );

    const inputElement = (
      <div className="relative">
        {leftIcon && (
          <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
            {leftIcon}
          </div>
        )}
        <input
          id={inputId}
          type={type}
          className={inputClasses}
          ref={ref}
          aria-invalid={ariaInvalid ?? (hasError || undefined)}
          aria-describedby={describedBy || undefined}
          {...props}
        />
        {rightIcon && (
          <div className="absolute inset-y-0 end-0 flex min-w-8 items-center justify-center pe-2">
            {rightIcon}
          </div>
        )}
      </div>
    );

    if (label || errorMessage || helperContent) {
      return (
        <div className={containerClasses}>
          {label && (
            <label
              htmlFor={inputId}
              className={labelClasses}
              style={labelStyle}
            >
              {label}
            </label>
          )}
          {inputElement}
          {errorMessage && (
            <p id={errorId} role="alert" className="text-error mt-1 text-sm">
              {errorMessage}
            </p>
          )}
          {helperContent && !errorMessage && (
            <p
              id={descriptionId}
              className="text-muted-foreground mt-1 text-sm"
            >
              {helperContent}
            </p>
          )}
        </div>
      );
    }

    return inputElement;
  },
);
Input.displayName = "Input";

export { Input };
export default Input;
